import { useState, useEffect } from 'react'
import {X, FileText, Loader2} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import { DEFAULT_SETTINGS } from '@/hooks/usePlatformSettings'

export type LegalDocKey = 'privacyPolicy' | 'reportsPolicy' | 'evaluationsPolicy' | 'termsAndConditions'

const DOC_TITLES: Record<LegalDocKey, string> = {
  privacyPolicy: 'Aviso de Privacidad',
  reportsPolicy: 'Reglamento de Reportes y Evidencias',
  evaluationsPolicy: 'Política de Evaluaciones y Reputación Laboral',
  termsAndConditions: 'Términos y Condiciones',
}

interface Props {
  docKey: LegalDocKey
  children: React.ReactNode
  className?: string
}

export function LegalDocLink({ docKey, children, className }: Props) {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [loading, setLoading] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const res = await lumi.entities.platform_settings.list({ filter: { key: 'global' }, limit: 1 })
      const doc = res?.list?.[0]
      setText((doc?.[docKey] as string) || DEFAULT_SETTINGS[docKey] || '')
    } catch {
      setText(DEFAULT_SETTINGS[docKey] || '')
    } finally {
      setLoading(false)
    }
  }

  const handleOpen = (e: React.MouseEvent) => {
    e.preventDefault()
    setOpen(true)
    if (!text) load()
  }

  useEffect(() => {
    if (open && !text && !loading) load()
  }, [open])

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className={className ?? 'text-amber-600 underline underline-offset-2 hover:text-amber-700 transition-colors'}
      >
        {children}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="relative flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            {/* Header */}
            <div className="flex items-center gap-3 border-b border-slate-200 bg-slate-50 px-6 py-4">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100">
                <FileText size={18} className="text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="truncate text-base font-bold text-slate-900">{DOC_TITLES[docKey]}</h2>
                <p className="text-xs text-slate-500">Beeznz Work</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 size={24} className="animate-spin text-amber-400" />
                </div>
              ) : (
                <div className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                  {text || 'Documento no disponible.'}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-slate-200 bg-slate-50 px-6 py-3 text-right">
              <button
                onClick={() => setOpen(false)}
                className="rounded-lg bg-slate-900 px-5 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
